/**
 * IdleSignOut: mounted once a person is signed in. Any pointer, key,
 * scroll or touch in any tab stamps the shared clock (typing in an open
 * note counts). At 28 minutes without activity a sheet asks "Still
 * here?" with a countdown; at 30 the person is signed out (src/lib/
 * signOut.ts) and lands on the sign-in screen, which says why. A tab that
 * comes back to the front after the limit has already passed signs out
 * at once, before treating the return as activity.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import {
  ACTIVITY_KEY,
  formatCountdown,
  idleState,
  readLastActivity,
  writeLastActivity,
  type IdleState,
} from '@/lib/idleSignOut';
import { signOut as signOutOfOlive } from '@/lib/signOut';

/** Activity is stamped at most this often; a keystroke every second
 *  must not write storage every second. */
const STAMP_INTERVAL_MS = 5_000;
const TICK_MS = 1_000;
const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export interface IdleSignOutProps {
  /** Replaced in tests; the default ends the Olive session and lands
   *  on the sign-in screen with the idle reason. */
  signOut?: () => void;
}

export function IdleSignOut({ signOut }: IdleSignOutProps): ReactNode {
  const [state, setState] = useState<IdleState>({ kind: 'active' });
  const storageRef = useRef<Storage | null>(null);
  const lastStampRef = useRef(0);
  const signedOutRef = useRef(false);
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  const doSignOut = useCallback((): void => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    if (signOutRef.current) signOutRef.current();
    else void signOutOfOlive('idle');
  }, []);

  const evaluate = useCallback((): void => {
    const last = readLastActivity(storageRef.current) ?? lastStampRef.current;
    const next = idleState(last, Date.now());
    if (next.kind === 'expired') {
      doSignOut();
      return;
    }
    setState((prev) =>
      prev.kind === next.kind && (next.kind !== 'warning' || prev.kind !== 'warning' || prev.remainingMs === next.remainingMs)
        ? prev
        : next,
    );
  }, [doSignOut]);

  const touch = useCallback(
    (force = false): void => {
      const now = Date.now();
      if (!force && now - lastStampRef.current < STAMP_INTERVAL_MS) return;
      lastStampRef.current = now;
      writeLastActivity(storageRef.current, now);
      setState({ kind: 'active' });
    },
    [],
  );

  useEffect(() => {
    storageRef.current = safeStorage();
    touch(true);

    const onActivity = (): void => touch();
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true, capture: true });

    // Another tab's activity resets this one; the stamp is the clock.
    const onStorage = (e: StorageEvent): void => {
      if (e.key === ACTIVITY_KEY) {
        const last = readLastActivity(storageRef.current);
        if (last !== null) lastStampRef.current = last;
        evaluate();
      }
    };
    window.addEventListener('storage', onStorage);

    // Coming back to the tab: if the limit already passed while it was
    // hidden (a closed laptop), sign out now; otherwise the return counts.
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      const last = readLastActivity(storageRef.current) ?? lastStampRef.current;
      if (idleState(last, Date.now()).kind === 'expired') {
        doSignOut();
        return;
      }
      touch(true);
    };
    document.addEventListener('visibilitychange', onVisibility);

    const timer = window.setInterval(evaluate, TICK_MS);
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, { capture: true });
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [touch, evaluate, doSignOut]);

  if (state.kind !== 'warning') return null;

  return (
    <Dialog
      title="Still here?"
      role="alertdialog"
      size="sm"
      onClose={() => touch(true)}
      testId="idle-warning"
      footer={
        <Button onClick={() => touch(true)} data-testid="idle-keep-signed-in">
          Keep me signed in
        </Button>
      }
    >
      <p className="text-sm text-ink">
        To keep client records private, you are signed out after 30 minutes without activity.
      </p>
      <p className="mt-2 text-sm text-ink-soft" role="status" aria-live="polite">
        Signing out in <span className="tabular-nums font-semibold text-ink">{formatCountdown(state.remainingMs)}</span>.
      </p>
    </Dialog>
  );
}
